/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';

/**
 * Lightweight Markdown renderer for AI insight briefs.
 * Escapes HTML; supports headings, lists, bold/italic, code, paragraphs.
 * Avoids adding react-markdown as a dependency for this surface.
 */
const inlineFormat = (text) => {
  const parts = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = re.exec(text)) != null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={`b-${key++}`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*')) {
      parts.push(<em key={`i-${key++}`}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith('`')) {
      parts.push(
        <code key={`c-${key++}`} className="spacer-log-md__code">
          {token.slice(1, -1)}
        </code>
      );
    }
    last = match.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
};

const SpacerLogMarkdown = ({ content }) => {
  const nodes = useMemo(() => {
    const raw = String(content || '').replace(/\r\n/g, '\n').trim();
    if (!raw) return [<p key="empty" className="spacer-log-md__muted">No content.</p>];

    const lines = raw.split('\n');
    const out = [];
    let listBuf = [];
    let listType = null;
    let key = 0;

    const flushList = () => {
      if (!listBuf.length) return;
      const Tag = listType === 'ol' ? 'ol' : 'ul';
      out.push(
        <Tag key={`list-${key++}`} className="spacer-log-md__list">
          {listBuf.map((item, i) => (
            <li key={i}>{inlineFormat(item)}</li>
          ))}
        </Tag>
      );
      listBuf = [];
      listType = null;
    };

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) {
        flushList();
        return;
      }

      const ul = trimmed.match(/^[-*]\s+(.+)$/);
      const ol = trimmed.match(/^\d+\.\s+(.+)$/);
      if (ul) {
        if (listType && listType !== 'ul') flushList();
        listType = 'ul';
        listBuf.push(ul[1]);
        return;
      }
      if (ol) {
        if (listType && listType !== 'ol') flushList();
        listType = 'ol';
        listBuf.push(ol[1]);
        return;
      }

      flushList();

      if (trimmed.startsWith('### ')) {
        out.push(
          <h4 key={`h-${key++}`} className="spacer-log-md__h4">
            {inlineFormat(trimmed.slice(4))}
          </h4>
        );
        return;
      }
      if (trimmed.startsWith('## ')) {
        out.push(
          <h3 key={`h-${key++}`} className="spacer-log-md__h3">
            {inlineFormat(trimmed.slice(3))}
          </h3>
        );
        return;
      }
      if (trimmed.startsWith('# ')) {
        out.push(
          <h2 key={`h-${key++}`} className="spacer-log-md__h2">
            {inlineFormat(trimmed.slice(2))}
          </h2>
        );
        return;
      }
      if (trimmed.startsWith('> ')) {
        out.push(
          <blockquote key={`q-${key++}`} className="spacer-log-md__quote">
            {inlineFormat(trimmed.slice(2))}
          </blockquote>
        );
        return;
      }

      out.push(
        <p key={`p-${key++}`} className="spacer-log-md__p">
          {inlineFormat(trimmed)}
        </p>
      );
    });

    flushList();
    return out;
  }, [content]);

  return <div className="spacer-log-md">{nodes}</div>;
};

export default SpacerLogMarkdown;
